import React from 'react'

import {
  FiltersBasic,
  type FiltersBasicPopoverProps,
  SelectOption,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

export interface Props {
  fields: readonly FilterOption[]
  onApplyFilters: (form?: MultiFilterForm[]) => void
  getFilterQuantity: (selectedFilter: string, selectedOption: string) => number
  getAppliedFilter: () => MultiFilterForm['values'] | undefined
}

const DEFAULT_FILTER_VALUE = 'status'
const DEFAULT_OPTION_VALUE = 'isActive'

const Filters = ({ fields, onApplyFilters, getFilterQuantity, getAppliedFilter }: Props) => {
  const appliedFilter = getAppliedFilter()
  const [filterPopoverOpen, setFilterPopoverOpen] = React.useState(false)
  const [subfilterPopoverOpen, setSubfilterPopoverOpen] = React.useState(false)
  const [selectedFilterValue, setSelectedFilterValue] = React.useState(appliedFilter?.field)
  const [selectedOptionValue, setSelectedOptionValue] = React.useState(appliedFilter?.value)

  const selectedFilter = fields.find((x) => x.value === selectedFilterValue)

  const handleFilterSelection = (filter: FilterOption) => {
    setSelectedFilterValue(filter.value)
    trackFilterSelection(filter.value)
    setSubfilterPopoverOpen(true)
  }

  const trackFilterSelection = (filter: string) => {
    if (filter === 'status') {
      track(trackingEvents.jobCatalog.filterByRoleStatus)
    }
  }

  const handleOptionSelection = (option: SelectOption, optionFilter: FilterOption) => {
    setSelectedOptionValue(option.value)
    applyFilter(optionFilter.value, option.value)
    setSubfilterPopoverOpen(false)
    setFilterPopoverOpen(false)
  }

  const applyFilter = (field: string, option: string) => {
    const filterForm = new MultiFilterForm({
      condition: CONDITIONS_VALUES.isEqual,
      field: field,
      value: option,
    })
    onApplyFilters([filterForm])
  }
  const filterOptions = fields.map((x) => {
    const isSelected = selectedFilterValue === x.value
    return (
      <FiltersBasic.Option
        key={x.value}
        isSelected={isSelected}
        onClick={() => handleFilterSelection(x)}
      >
        {x.label}
      </FiltersBasic.Option>
    )
  })
  const filterOptionItems = (selectedFilter?.options ?? []).map((x) => {
    const isSelected = selectedOptionValue === x.value
    return (
      <FiltersBasic.Option
        key={x.value}
        isSelected={isSelected}
        onClick={() => handleOptionSelection(x, selectedFilter as FilterOption)}
      >
        {x.label}
      </FiltersBasic.Option>
    )
  })

  const handleReset = () => {
    setFilterPopoverOpen(false)
    setSubfilterPopoverOpen(false)
    setSelectedFilterValue('')
    setSelectedOptionValue('')
    applyFilter(DEFAULT_FILTER_VALUE, DEFAULT_OPTION_VALUE)
  }

  const commonProps: Pick<FiltersBasicPopoverProps, 'title' | 'count' | 'blankLabel'> = {
    title: i18n.t('common.filter'),
    count: getFilterQuantity(selectedFilterValue, selectedOptionValue),
    blankLabel: i18n.t('filters.default_filters.filter_not_found'),
  }

  const optionsHeader = selectedFilter?.label ?? i18n.t('filters.default_filters.filter_by')
  const handleFilterOpenChange = (isOpen: boolean) => {
    setFilterPopoverOpen(isOpen)
  }

  const handleSubFilterOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSubfilterPopoverOpen(isOpen)
      setFilterPopoverOpen(isOpen)
    }
  }

  return subfilterPopoverOpen ? (
    <FiltersBasic.Popover
      isOpen={subfilterPopoverOpen}
      icon="chevron-left"
      header={optionsHeader}
      onClickAction={() => setSubfilterPopoverOpen(false)}
      onOpenChange={handleSubFilterOpenChange}
      missingLabel={i18n.t('common.missing_field')}
      selectValue={i18n.t('common.select')}
      {...commonProps}
    >
      {filterOptionItems}
    </FiltersBasic.Popover>
  ) : (
    <FiltersBasic.Popover
      isOpen={filterPopoverOpen}
      header={i18n.t('filters.default_filters.filter_by')}
      reset={i18n.t('filters.default_filters.reset_filters')}
      onClickReset={handleReset}
      onOpenChange={handleFilterOpenChange}
      missingLabel={i18n.t('common.missing_field')}
      selectValue={i18n.t('common.select')}
      {...commonProps}
    >
      {filterOptions}
    </FiltersBasic.Popover>
  )
}
export default observer(Filters)
